import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useTranslations } from 'use-intl';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { TaxAccountApiClient } from '@/clients/taxAccountApiClient';
import TaxCheckAgain from './TaxCheckAgain';

interface TaxAccountRowActionsProps {
  accountId: string;
  accountLabel: string;
  /** Offer "Finish": the setup is unfinished, and the next step is a fresh trip to the authority. */
  finish: boolean;
  /** Offer "Discard": never finished and never read, as the server judged it. */
  discard: boolean;
  /** Offer "Check again": the authority reports no skattekonto for the company yet. */
  checkAgain: boolean;
  /** Named in the sentence a still-nothing check answers with. */
  authorityName: string;
  /** Finish needs the company's connect details; without them it is not offered. */
  canConnect: boolean;
  /** Opens the section's one connect dialog — the row itself fetches nothing. */
  onConnect: () => void;
  /** Reloads the accounts, so the list shows what the server now holds. */
  onAccountsChanged: () => void | Promise<void>;
}

/**
 * The actions a consultant's tax row may carry (FIN-1065): Finish and Discard on an unfinished
 * setup (screens §3a, scenarios C and N), and Check again on a row waiting for a skattekonto
 * (FIN-1453), decided by the rules shared with apps/client's row.
 *
 * No Reconnect here, unlike the client's row: this section's header already offers Reconnect for
 * a revoked account, and so does the detail page — a third button for the same dialog is noise.
 */
const TaxAccountRowActions: React.FC<TaxAccountRowActionsProps> = ({
  accountId,
  accountLabel,
  finish,
  discard,
  checkAgain,
  authorityName,
  canConnect,
  onConnect,
  onAccountsChanged,
}) => {
  const t = useTranslations('consultantTax.section.rowActions');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const discardDraft = useMutation({
    mutationFn: async () => {
      const outcome = await TaxAccountApiClient.discardDraft(accountId);
      // Inside the mutation, so "Discarding…" holds until the row is actually gone.
      if (outcome === 'discarded') await onAccountsChanged();
      return outcome;
    },
  });

  const showFinish = finish && canConnect;
  // An open confirmation stays mounted even if a reload meanwhile stops offering Discard, so a
  // refusal the consultant is still reading never vanishes under them.
  if (!showFinish && !discard && !checkAgain && !confirmOpen) return null;

  // The server's refusal is an answer, shown in place. Any other failure is reported and toasted
  // with its support reference by the API client; Discard stays enabled to try again.
  const refused = discardDraft.data === 'connected';

  const handleOpenChange = (next: boolean) => {
    if (discardDraft.isPending) return;
    setConfirmOpen(next);
    if (next) return;
    // A refusal means the account is not what this row showed; reload only once the consultant
    // has read why, so the sentence does not vanish with the row under it.
    if (refused) void onAccountsChanged();
    discardDraft.reset();
  };

  return (
    <div className="flex shrink-0 items-center gap-2">
      {discard && (
        <Button
          variant="ghost"
          size="sm"
          data-testid="tax-account-discard"
          onClick={() => handleOpenChange(true)}
        >
          {t('discard')}
        </Button>
      )}
      {checkAgain && (
        <TaxCheckAgain
          accountId={accountId}
          authorityName={authorityName}
          onChecked={onAccountsChanged}
          testIdPrefix="tax-account"
        />
      )}
      {showFinish && (
        <Button variant="secondary" size="sm" data-testid="tax-account-finish" onClick={onConnect}>
          {t('finish')}
        </Button>
      )}
      {/* An alert dialog, src/'s primitive for a destructive confirmation: it asks for an explicit
          answer and is not dismissed by a stray click outside it. */}
      <AlertDialog open={confirmOpen} onOpenChange={handleOpenChange}>
        <AlertDialogContent data-testid="tax-discard-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('discardTitle', { account: accountLabel })}</AlertDialogTitle>
            <AlertDialogDescription>{t('discardBody')}</AlertDialogDescription>
          </AlertDialogHeader>
          {refused && (
            <Alert data-testid="tax-discard-error">
              <AlertDescription>{t('refusedConnected')}</AlertDescription>
            </Alert>
          )}
          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={discardDraft.isPending}
            >
              {t('keep')}
            </Button>
            <Button
              variant="destructive"
              data-testid="tax-discard-confirm"
              onClick={() => discardDraft.mutate()}
              disabled={discardDraft.isPending || refused}
            >
              {discardDraft.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {discardDraft.isPending ? t('discarding') : t('confirm')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TaxAccountRowActions;
